// @ts-strict-ignore
import React from 'react';
import type {
  ReactFlowProps,
  Viewport,
} from 'react-flow-renderer';
import {
  useReactFlow,
} from 'react-flow-renderer';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { ON_MOVE_END_DEBOUNCE_TIME, ON_MOVE_START_DEBOUNCE_TIME } from './constants';
import type { WorkflowNode } from 'flow/service/graph';
import { selectCanvasRect, selectCanvasTranslateExtent } from 'flow/store/redux/selectors';
import { useAppSelector } from '~/store/reduxHooks';


/**
 *
 * There are several bugs in react-flow v10 related to  moving the canvas when the `panOnScroll`
 * prop is passed as `true`. Mainly:
 * - the onMoveStart and onMoveEnd events to behave like onMove
 * (firing multiple times as the user moves around instead of only once when started and finished).
 * - multiple onMove events with the same viewport values are triggered when scrolling into a hard
 * canvas bound (set by the `translateExtent` prop).
 * - the onMoveEnd event is not always called when scrolling into a hard bound.
 *
 * This hook creates an onMove event that simulates the expected behavior of the onMoveStart and
 * onMoveEnd events using only the onMove event. This is necessary since react-flow does not
 * always call the onMoveEnd event.
 *
 * To simulate the onMoveStart and onMoveEnd we debounce the events fired by react-flow.
 * Debouncing allows us to "batch" repeated events into a single one, only after X amount
 * of time has passed in between them (ms in this case) and can be done on a leading vs
 * trailing edge (start of batch vs end of batch).
 *
 * To prevent acting on repeated onMoveEvents, we keep a ref to the previous Viewport value
 * and only call our debounced callbacks if the value changes.
 *
 * Bug issue - https://github.com/wbkd/react-flow/issues/675
 */
export function useReactFlowOnMove({ start, end }: { start: ReactFlowProps['onMoveStart']; end: ReactFlowProps['onMoveEnd'] }) {
  const { getViewport } = useReactFlow();
  const lastViewport = React.useRef<Viewport>(getViewport());

  const handleOnMoveStart: ReactFlowProps['onMoveStart'] = React.useMemo(() => debounce(start,
    ON_MOVE_START_DEBOUNCE_TIME,
    { leading: true, trailing: false }), [start]);

  const handleOnMoveEnd: ReactFlowProps['onMoveEnd'] = React.useMemo(() => debounce(end,
    ON_MOVE_END_DEBOUNCE_TIME), [end]);

  const handleOnMove: ReactFlowProps['onMove'] = React.useCallback((e, viewport) => {
    // do nothing if getting the same viewport value
    if (isEqual(lastViewport.current, viewport)) return;

    handleOnMoveStart(e, viewport);
    handleOnMoveEnd(e, viewport);
    lastViewport.current = viewport;
  }, [handleOnMoveStart, handleOnMoveEnd]);


  return handleOnMove;
}

export function usePanReactFlowToNode() {
  const { getViewport, setViewport } = useReactFlow();

  const [
    [originX, originY], [largestPossibleX, largestPossibleY],
  ] = useAppSelector(selectCanvasTranslateExtent);

  const {
    width: canvasWidth, height: canvasHeight,
  } = useAppSelector(selectCanvasRect);

  const handleFocusToNode = React.useCallback((targetNode: WorkflowNode) => {
    const { position: { x: nodeX, y: nodeY } } = targetNode;

    /**
     * The math for this is as follows:
     * - calculate an unbounded vector (x,y) for the new position, which is done by using the
     * screen canvas center as the target point, and subtracting the current (x,y) for the node.
     * - then, both points get bounded and clipped (if needed) by the allowed panning space,
     * ensuring that we don't pan past the canvas translate extent in any of the four directions.
     *
     * it's important to note that the translate extent is defined as [[x0,y0] , [x1, y1]], and it
     * represents the viewport bounds. meanwhile, the canvas coordinates moves the origin
     * (the top-left corner) of the canvas to the desired position. so even if we have a translate
     * extent of [[0, 0], [1000, 1000]], a new coordinate with the value of (-100,-100) can exist
     * for the canvas origin (the top-left point of the canvas).
     */

    // set the middle of the screen as the desired X coordinate
    const canvasCenterX = canvasWidth / 2;
    const desiredX = canvasCenterX - nodeX;

    const minX = -(Math.abs(canvasWidth - largestPossibleX));
    const maxX = originX;
    // make sure the desiredX stays within the min and max values allowed, or clip if it doesn't
    const newX = Math.max(minX, Math.min(desiredX, maxX));

    // set the middle of the screen as the desired Y coordinate
    const canvasCenterY = canvasHeight / 2;
    const desiredY = canvasCenterY - nodeY;

    const minY = -(Math.abs(canvasHeight - largestPossibleY));
    const maxY = originY;

    // make sure the desiredY stays within the min and max values allowed, or clip if it doesn't
    const newY = Math.max(minY, Math.min(desiredY, maxY));

    const { zoom: currentZoom } = getViewport();
    setViewport({ x: newX, y: newY, zoom: currentZoom });
  }, [
    getViewport, setViewport,
    canvasWidth, canvasHeight,
    originX, originY,
    largestPossibleX, largestPossibleY,
  ]);

  return handleFocusToNode;
}
